/**
 * Runtime store for the experiment a visitor has been bucketed into.
 *
 * Replaces the build-time `AB_TEST_NAME` / `AB_TEST_VARIANT` constants. Webpack
 * inlined those as string literals at build time, which is why changing a test
 * used to need a Netlify rebuild. LaunchDarkly resolves the variant in the
 * browser instead, so consumers have to read it at call time rather than
 * capturing it at module load.
 *
 * The initial values are still seeded from the Netlify variables. That keeps
 * the branch-based split working untouched while both mechanisms coexist, and
 * lets us decommission it without a flag-day cutover.
 *
 * NOTE: If we ever remove this file, the four consumers below need updating:
 * services/Analytics.js, services/Registration.js, hooks/useCurrentUser.js,
 * and utils/netlifyTest.ts.
 */
import { readonly, reactive } from '@nuxtjs/composition-api';

interface ExperimentState {
  // False until LaunchDarkly has resolved (or has failed and we fell back).
  ready: boolean;
  // Reported to Segment as `test-name`.
  name: string;
  // Reported to Segment as `test-variant`.
  variant: string;
  // Every resolved flag value, keyed by flag key.
  flags: Record<string, string>;
}

const state = reactive<ExperimentState>({
  ready: false,
  name: process.env.AB_TEST_NAME || '',
  variant: process.env.AB_TEST_VARIANT || '',
  flags: {},
});

export const setExperiment = (name: string, variant: string): void => {
  state.name = name;
  state.variant = variant;
};

export const setFlags = (flags: Record<string, string>): void => {
  state.flags = { ...state.flags, ...flags };
};

export const setReady = (ready = true): void => {
  state.ready = ready;
};

export const getFlag = (key: string, fallback = ''): string =>
  state.flags[key] ?? fallback;

// Plain getters for the non-Vue consumers (Analytics, Registration) that need
// the current value at call time and cannot hold a reactive reference.
export const getExperimentName = (): string => state.name;
export const getExperimentVariant = (): string => state.variant;

const useExperiment = () => ({
  experiment: readonly(state),
  getFlag,
  setExperiment,
  setFlags,
  setReady,
});

export default useExperiment;
